/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../core/components';
import { Component } from '@angular/core';
import { DropdownModel, ErrorResponseModel } from '../../../../../core/models';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { IpGroupApiService, ServicesApiService, ShuttleApiService, SIPApiService } from '../../../shared/http';
import {
    LogService,
    ModalService,
    NotifyService,
    RbacService,
    UtilsService,
    ValidationService
} from '../../../../../core/services';
import { concatMap, takeUntil } from 'rxjs/operators';
import { domainOrIpValidation, portRangeValidation } from '../../../../../core/directives';
import { forkJoin, Observable, Subscriber } from 'rxjs';

@Component({
    selector: 'app-portal-management',
    templateUrl: './portal-management.component.html',
    styleUrls: ['./portal-management.component.scss']
})
export class PortalManagementComponent extends BaseComponent {
    /**
     * Flag to state the API is loaded.
     */
    public loaded: boolean = false;

    /**
     * Store the current step.
     */
    public activeStep: number = 1;

    /**
     * New Loadbalancer FormGroup
     */
    public formGroup: FormGroup;

    /**
     * Store the adoption status.
     */
    public adoptionState: number = 1;

    /**
     * Static IP list.
     */
    public dropdownContent: DropdownModel[] = [];

    /**
     * Store the interfaces that can be bonded
     */
    public subnets: any[] = [];

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    /**
     * To store the initial data for the date and time form.
     * @private
     */
    private initialStep1FormData: any = {};

    constructor(
        private formBuilder: FormBuilder,
        private ipGroupApiService: IpGroupApiService,
        private logService: LogService,
        private modalService: ModalService,
        private notifyService: NotifyService,
        private rbacService: RbacService,
        private servicesApiService: ServicesApiService,
        private shuttleApiService: ShuttleApiService,
        private sipApiService: SIPApiService,
        private utilsService: UtilsService
    ) {
        super();
        this.setUpForm();
        this.subscribeToFormChanges();
        this.setUpPageData();
        // this.registerButtons(); Not here due to timing issues
    }

    get getSubnetIdFields() {
        return this.formGroup.get('step1.subnetIds') as FormArray;
    }

    /**
     * Returns the form group as FormGroup.
     * @param group
     */
    public getFormGroup(group: any): FormGroup {
        return this.formGroup.get(group) as FormGroup;
    }

    /**
     * Convert control to type FormControl
     * @param control
     */
    public getFormControl(control: any): FormControl {
        return control as FormControl;
    }

    /**
     * Go to a specific step in the process.
     * @param number
     */
    public setStep(number: number): void {
        this.activeStep = number;
    }

    /**
     * Check if the button is disabled based on the form step state.
     */
    public isNextButtonDisabled(): boolean {
        const activeStep: AbstractControl = this.formGroup.get('step' + this.activeStep);

        return (activeStep.invalid || activeStep.pristine);
    }

    /**
     * Submit shuttle data.
     */
    public submitShuttle(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.notifyService.muteLocalNotifications();
            const shuttleData = this.formGroup.get('step1').value;
            shuttleData.subnetIds = this.getSubnetIds(shuttleData);
            this.shuttleApiService.setShuttle(shuttleData)
                .pipe(
                    concatMap(() => this.servicesApiService.restartService('lb-sidecar')),
                    takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.notifyService.unmuteLocalNotifications();
                        this.notifyService.success('portal_management_shuttle_setup', { messageTranslate: true });
                        this.activeStep = 2;
                        this.adoptionState = 2;
                        this.utilsService.clearValidatorOnAutoFill(this.formGroup.get('step2') as FormGroup);
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Put loadbalancer up for adoption.
     */
    public adopt(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const shuttleLoginData = this.formGroup.get('step2').value;
            this.shuttleApiService.adopt(shuttleLoginData)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.activeStep = 3;
                        this.adoptionState = 3;
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Confirm delete FIP group.
     */
    public confirmResetShuttle(): void {
        const confirmationObject = {
            title: 'portal_management_remove_adc',
            message: 'portal_management_remove_adc_message',
            callBack: this.resetShuttle.bind(this),
            options: { translateText: true }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Put loadbalancer up for adoption.
     * @private
     */
    private resetShuttle(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const subnetIds = this.getSubnetIdFields.value.map((item: boolean) => item === false);
            const shuttleData = {
                enabled: false,
                portalAddress: 'portal.loadbalancer.org',
                portalPort: 443,
                managementIPId: '',
                subnetIds
            };
            this.shuttleApiService.setShuttle(shuttleData)
                .pipe(
                    concatMap(() => this.servicesApiService.restartService('lb-sidecar')),
                    takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.setUpPageData();
                        this.activeStep = 1;
                        this.adoptionState = 1;
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Set up the add load balancer form.
     * @private
     */
    private setUpForm(): void {
        this.formGroup = this.formBuilder.group({
            step1: this.formBuilder.group({
                enabled: [null, Validators.required],
                portalAddress: [null, [Validators.required, domainOrIpValidation]],
                portalPort: [null, [Validators.required, portRangeValidation]],
                managementIPId: [null, Validators.required],
                subnetIds: new FormArray([], { validators: ValidationService.atLeastOneRequired })
            }),
            step2: this.formBuilder.group({
                email: [null, [Validators.required, ValidationService.emailValidator]],
                password: [null, [Validators.required]],
            }),
            step3: this.formBuilder.group({
                adopted: true
            })
        });
    }

    /**
     * Subscribe to the form changes.
     * @private
     */
    private subscribeToFormChanges(): void {
        this.formGroup.get('step1').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: values => {
                    this.utilsService.debounce('formGroup', () => {
                        if (!this.utilsService.hasFormDataChanged(this.initialStep1FormData, values)) {
                            this.formGroup.markAsPristine();
                        } else {
                            this.formGroup.markAsDirty();
                        }
                    });
                }
            });
    }

    /**
     * Set up page data with API response.
     * @private
     */
    private setUpPageData(): void {
        this.notifyService.muteLocalNotifications();
        // Clear subnets, used on reset.
        this.getSubnetIdFields.controls = [];

        forkJoin({
            shuttleDetails: this.shuttleApiService.getShuttle(),
            connectionStatus: this.shuttleApiService.getConnectionStatus(),
            groups: this.ipGroupApiService.getGroups(),
            staticIps: this.sipApiService.getStaticIPs(),
        })
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    this.loaded = true;
                    this.setView(data);
                    this.setUpFormElements(data);
                    this.patchFormValues(data);
                    this.notifyService.unmuteLocalNotifications();
                    this.registerButtons();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('AddLoadbalancer:setUpPageData', error.message);
                }
            });
    }

    /**
     * Set the view based on API data.
     * @param data
     * @private
     */
    private setView(data: any): void {
        if (data['shuttleDetails']?.enabled) {
            this.activeStep = 2;
            this.adoptionState = 2;
        }

        if (data['connectionStatus']?.adopted) {
            this.activeStep = 3;
            this.adoptionState = 3;
        }
    }

    /**
     * Set up form elements (drop-downs and groups)
     * @param data
     * @private
     */
    private setUpFormElements(data: any): void {
        this.dropdownContent = this.utilsService.convertArrayToDropDown(data['staticIps'], 'id', 'ip');
        this.createSubnetIdFormControls(data);
    }

    /**
     * Create a form controller for each group
     * @param data
     * @private
     */
    private createSubnetIdFormControls(data: any[]): void {
        const subnetsIds: any = data['shuttleDetails']?.subnetIds;
        this.subnets = data['groups'];
        for (let i = 0; i < this.subnets.length; i++) {
            this.getSubnetIdFields.push(new FormControl(subnetsIds.includes(this.subnets[i].id)));
        }
    }

    /**
     * Patch API data to form.
     * @param data
     * @private
     */
    private patchFormValues(data: any): void {
        const shuttleDetails = data['shuttleDetails'];
        const formData = {
            enabled: !!shuttleDetails.enabled,
            portalAddress: shuttleDetails.portalAddress,
            portalPort: shuttleDetails.portalPort,
            managementIPId: shuttleDetails.managementIPId !== '' ? shuttleDetails.managementIPId : null,
        };

        this.formGroup.get('step1').patchValue(formData);
        this.initialStep1FormData = this.formGroup.get('step1').value;
    }

    /**
     * Get subnet ids from form map.
     * @param shuttleData
     * @private
     */
    private getSubnetIds(shuttleData: any): string[] {
        const subnetIds: any[] = [];
        const formBooleans = shuttleData['subnetIds'];
        for (let i = 0; i < formBooleans.length; i++) {
            if (formBooleans[i]) {
                subnetIds.push(this.subnets[i].id);
            }
        }

        return subnetIds;
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'management-submit-shuttle': [
                { path: '/portal/sidecar', methods: ['PUT'] },
                { path: '/services/:service/restart', methods: ['PUT'] }
            ],
            'management-submit-adopt': [{ path: '/portal/adopt', methods: ['PUT'] }],
            'management-remove': [
                { path: '/portal/sidecar', methods: ['PUT'] },
                { path: '/services/:service/restart', methods: ['PUT'] }
            ]
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
        if (this.rbacService.disableForm(this.permissionsLookup)) {
            this.formGroup.disable();
        }
    }
}

